import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    componentPartLabels,
    formatMillis,
    formatMultiplier,
    kindLabel,
    policyKind,
    policyScope,
} from '@netfoundry-ui/feature/form/latency-policy-form';
import {
    ladderSummary,
    offlineComponentIds,
    offlinePolicyScope,
    OFFLINE_RUNGS,
} from '@netfoundry-ui/feature/form/offline-policy-form';
import { AssistantChatService } from '@netfoundry-ui/feature/assistant-chat';
import { AlertServiceV2, AlertTypeRegistry } from '@netfoundry-ui/shared/apiv2';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import {
    AlertEventView,
    AlertSeverity,
    AlertView,
    ClaimRequest,
    LatencyPolicy,
    Mute,
    MuteRequest,
    NamedComponent,
    OfflinePolicy,
    Person,
    Timeline,
} from '@netfoundry-ui/shared/model';
import { LoggerService, URLS } from '@netfoundry-ui/shared/services';
import { formatIsoDuration } from '@netfoundry-ui/util/duration';
import { isEmpty, isNil, isPlainObject } from 'lodash';
import { alertTypeLabel } from './alert-type-labels';
import { explainAlertPrompt } from './explain-alert-prompt';
import { ClaimAlertDialogComponent, ClaimAlertDialogData } from './claim-alert-dialog.component';
import {
    GoverningPolicyKind,
    governingPolicyHref,
    governingPolicyId,
    governingPolicyKind,
    hasGoverningPolicy,
} from './policy-governance';
import { MuteAlertDialogComponent, MuteAlertDialogData } from './mute-alert-dialog.component';

/** One name/value row in the detail table. */
export interface DetailRow {
    /**
     * What the row is, independent of what it is called.
     *
     * The label is display text and changes with the wording; this does not. It is what
     * the template builds the row's `id` from and what a test reaches a row by, so a
     * reworded label moves nothing.
     */
    key: string;
    label: string;
    value: string;
    /**
     * The machine id behind {@link value}, shown after it and quieter than it.
     *
     * Only set where something readable names the same thing — the same rule
     * {@link DetailCell.id} follows one level down. An id nothing could name renders as the
     * value itself at full strength, because it is then the only handle the reader has.
     */
    id?: string;
    /** Ids, fingerprints and label values read better in a monospace column. */
    mono?: boolean;
    /**
     * Rendered instead of {@link value} when the value was a list of objects.
     *
     * Optional so every existing row literal stays a two-field object, and {@link value}
     * stays populated with the JSON fallback either way — anything reading rows as
     * strings still gets the whole value rather than an empty cell.
     */
    table?: DetailTable;
}

/** One cell of a {@link DetailTable}, and the id behind it when the cell is a name. */
export interface DetailCell {
    value: string;
    /**
     * The id this cell resolved from, shown under the name.
     *
     * Only set where a name was found for a `componentId` column. The id alone renders as
     * the value, because a reader who cannot be given a name is better off with the id
     * than with a blank.
     */
    id?: string;
    mono?: boolean;
    /** The severity this cell names, which sets its tone. */
    severity?: AlertSeverity;
    /** Whether this cell's row is the rung the alert stands on now. */
    current?: boolean;
}

/**
 * A structured value in `evidence`, rendered as a table rather than as a JSON string.
 *
 * A list of objects takes its columns from the union of the rows' keys in first-seen
 * order, so a row that carries an extra field widens the table rather than losing the
 * field. A map of scalars takes the two columns {@link MAP_COLUMNS} names.
 */
export interface DetailTable {
    columns: string[];
    rows: DetailCell[][];
    /**
     * How the table is drawn. `records` is a list of entities each with the same measured
     * columns (e.g. the latency directions) — shown as a stat block per entity. `map` is a
     * flat list of key/value scalars — shown as chips. `ladder` is a map from severity to
     * the threshold for it — shown as a table, least severe first.
     */
    variant: 'records' | 'map' | 'ladder';
}

/** Which pane the drawer is showing. */
export type DrawerTab = 'details' | 'source' | 'policy' | 'muting' | 'history' | 'advanced';

/**
 * The panes built from the alert itself, in the order they are shown.
 *
 * History and Policy are not here, because their content comes from a second request
 * rather than from the alert. Advanced is last on purpose: it holds what a ticket quotes
 * rather than what an incident needs.
 */
const DETAIL_TABS: DrawerTab[] = ['details', 'source', 'muting', 'advanced'];

/**
 * Every pane, in tab-strip order, with the label each is shown under.
 *
 * Policy follows Source because it answers the same question - Source is what the
 * detector saw, and Policy is the ladder that measurement was graded against. It is the
 * one pane that is not always offered; see {@link AlertDetailDrawerComponent.tabs}.
 */
export const DRAWER_TABS: { id: DrawerTab; label: string }[] = [
    { id: 'details', label: 'Details' },
    { id: 'source', label: 'Source' },
    { id: 'policy', label: 'Policy' },
    { id: 'muting', label: 'Muting' },
    { id: 'history', label: 'History' },
    { id: 'advanced', label: 'Advanced' },
];

/**
 * What each kind of event says, in words.
 *
 * The type is the sentence rather than a badge beside one: a reader scanning a timeline
 * is reading down the left edge, and `SEVERITY_CHANGED` in that column costs them a
 * translation on every row.
 */
const EVENT_TEXT: Record<string, string> = {
    OPENED: 'Opened',
    FIRED: 'Fired',
    SEVERITY_CHANGED: 'Severity changed',
    STALE: 'Stopped being seen',
    RECOVERED: 'Recovered',
    CLAIMED: 'Claimed',
    RELEASED: 'Released',
    RESOLVED: 'Resolved',
    /* "Muted at this instant", not "is muted now". The mute itself stays the answer to
     * whether the alert is silenced, and the History tab says only that somebody
     * silenced it here. */
    MUTED: 'Muted',
    /* Reads as "Mute revoked" rather than "Revoked": "Released" is already the word for
     * handing back a claim, and a lone "Revoked" beside it in the same column asks the
     * reader which of the two was undone.
     *
     * It does not pair off with the row above it. A mute that lapses on its own writes
     * nothing, so a Muted with nothing after it is a mute still in force or one that
     * expired — and a Mute revoked can stand alone, on an alert that opened under the
     * mute and so never got a Muted of its own. */
    MUTE_REVOKED: 'Mute revoked',
};

/**
 * Free-form keys the panes leave out.
 *
 * `networkName` answers no question the reader has, because the page is already scoped to
 * one network, and `severity` is the alert's own field the Alert block already shows - a
 * detector's copy of it can disagree, and the alert is the current one. Deliberately a set
 * of exact keys rather than a pattern: `labels` and `evidence` exist so a detector can add
 * a dimension without a migration, and a pattern would swallow the next field somebody
 * adds.
 */
const DROPPED_KEYS = new Set(['networkName', 'severity']);

/**
 * Keys that sort after every other key in a free-form block, whatever their name would do
 * alphabetically.
 *
 * `note` is prose the detector wrote about the rest of the block, so it sits under the
 * fields it talks about instead of in the middle of them. Exact keys, matched without
 * case, for the reason {@link DROPPED_KEYS} is a set of exact keys.
 */
const TRAILING_KEYS = new Set(['note', 'notes']);

/** A titled block of rows. Sections with no rows are dropped before rendering. */
export interface DetailSection {
    /** What the block is, independent of what it is titled. See {@link DetailRow.key}. */
    key: string;
    title: string;
    rows: DetailRow[];
}

/* What a field with no value renders as: nothing.
 *
 * A dash would be a mark the reader has to decode, and every value here sits beside its
 * own label - a row reading "Resolved" with nothing after it already says the alert is not
 * resolved. Telling "no value" apart from "the fetch failed" is never needed, because the
 * whole alert arrives in one response, so a rendered row is a read that succeeded. */
const EMPTY = '';

/* What stands where a component's name would be when no controller could answer for it.
 *
 * The id follows it on the same row either way, so the placeholder costs the reader
 * nothing: it says the name is missing, and the handle they would paste into a search or a
 * ticket is still right beside it. */
const UNNAMED_COMPONENT = 'unknown';

/**
 * Whether a value is a list of objects, which is the one shape rendered as a table.
 *
 * Narrow on purpose: a nested plain object keeps the JSON fallback, because the rule has to
 * stay derived from the shape rather than from which alert type is on screen. An empty list
 * is not a table - it would render as a heading with nothing under it, where `[]` at least
 * says what came back.
 */
function isRowList(value: any): boolean {
    return Array.isArray(value) && value.length > 0 && value.every((row) => isPlainObject(row));
}

/**
 * Whether a value is a map of scalars, which is the other shape rendered as a table.
 *
 * A map holding a list or another object keeps the JSON fallback, because one value column
 * would hold the same blob one level down. An empty map is not a table, for the reason an
 * empty list is not.
 */
function isScalarMap(value: any): boolean {
    return (
        isPlainObject(value) &&
        Object.keys(value).length > 0 &&
        Object.values(value).every((entry) => !isPlainObject(entry) && !Array.isArray(entry))
    );
}

/** Severities from least to most severe, which is the order a ladder escalates in. */
const SEVERITY_ORDER: AlertSeverity[] = ['INFO', 'MINOR', 'MAJOR', 'CRITICAL'];

/**
 * Whether a value is an escalation ladder: a map of scalars keyed only by severity names.
 * Decided by the value's shape rather than by the field name, like every other shape rule here.
 */
function isLadder(value: any): boolean {
    return isScalarMap(value) && Object.keys(value).every((key) => SEVERITY_ORDER.includes(key as AlertSeverity));
}

/* What a ladder is drawn under. Offline ladders are the only ones a detector sends today. */
const LADDER_COLUMNS = ['Severity', 'Offline for'];

/* What a map of scalars is drawn under. The keys are the detector's own - an error code,
 * an id, a hostname - so the header names the column rather than what is in it, and each
 * key is shown exactly as it was sent. */
const MAP_COLUMNS = ['Name', 'Count'];

/**
 * An ISO-8601 instant, which is the one free-form value shown in the reader's own zone.
 *
 * The zone designator is required - `Z`, or an offset - because without one the string
 * names no instant, and a browser reads a bare date-time as local already. A bare
 * `2026-09-05` is left alone for the same reason, and shifting a date with no time in it
 * can move it a day.
 */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/;

/**
 * Whether a value is an instant, which is the one free-form value restated in the reader's
 * own time zone.
 *
 * Derived from the shape of the value, never from the key: `evidence` and `labels` are
 * flattened fields so a detector can add a dimension with no migration, and a rule keyed on
 * names would go stale the moment one did. The parse is checked as well as the pattern, so
 * a well-shaped impossibility stays the string the detector wrote rather than rendering as
 * "Invalid Date".
 */
function isInstant(value: any): boolean {
    return typeof value === 'string' && ISO_INSTANT.test(value) && !isNaN(new Date(value).getTime());
}

/* The two keys an evidence row names its component with, matching what the alert itself
 * carries. Read by name in a table cell so a name can be shown where an id was sent - see
 * `cell`. They are the API's spelling, so they are constants rather than inline strings. */
const COMPONENT_TYPE_KEY = 'componentType';
const COMPONENT_ID_KEY = 'componentId';

/**
 * Columns an evidence table leaves out.
 *
 * Both are facts about the alert rather than about the row, so a detector that repeats
 * either per row buys a column of one value repeated down the table. Separate from
 * {@link DROPPED_KEYS} because that set filters the keys of a record and a column is a key
 * inside one of its values.
 */
const DROPPED_COLUMN_KEYS = new Set([COMPONENT_TYPE_KEY, 'severity']);

/**
 * The order a table's recognized columns are shown in: what the row is about, then the
 * baseline it is measured against, then the measurement, then the ratio between them -
 * the order the reader compares them in.
 *
 * Prefixes rather than exact keys, so `baseline`, `baselineMillis` and `baselineP95Millis`
 * all rank as the baseline. Anything unmatched keeps its first-seen order after these,
 * so an unrecognized field is late rather than lost.
 */
const COLUMN_ORDER: RegExp[] = [/^component(Name|Id)$/i, /^baseline/i, /^p95/i, /^times/i];

/**
 * Field names that are not the humanised key, wherever the field is named.
 *
 * A column header and a key/value row ask the same question, so one override answers both -
 * and a detector can write a field as a list of rows on one alert and as a scalar on
 * another. `timesBaseline` would humanize to "Times baseline", which reads as a count
 * rather than the ratio it holds, so it is named "Multiples of normal" instead.
 */
const FIELD_LABELS: Record<string, string> = {
    timesBaseline: 'Multiples of normal',
};

/**
 * Column headers that are not the field name, on top of {@link FIELD_LABELS}.
 *
 * The component column shows the resolved name with the id underneath, so "Component ID"
 * would name the fallback rather than the cell. **Header only**: a key/value row shows the
 * raw id, where "Component ID" is what it is.
 */
const COLUMN_LABELS: Record<string, string> = {
    [COMPONENT_ID_KEY]: 'Component',
    componentName: 'Component',
};

/* The units a detector spells out in a field name. Read off the end of a key and shown as
 * a suffix on the label instead, so "P95 millis" reads "P95 (ms)" - the unit belongs to
 * the column, and the name of the measurement is what the reader is scanning for. */
const UNIT_SUFFIXES: Record<string, string> = {
    millis: 'ms',
    milliseconds: 'ms',
    ms: 'ms',
};

/* Reads as "Edge router" rather than "EDGE_ROUTER". Kept beside the one place that renders
 * it, because a shared constant for four words would couple a page library to a drawer. */
const COMPONENT_TYPE_LABELS: Record<string, string> = {
    SERVICE: 'Service',
    EDGE_ROUTER: 'Edge router',
    LINK: 'Link',
    DETECTOR: 'Detector',
};

/* Words that are initialisms rather than words. Matched case-insensitively against each
 * word after the key has been split, so `routerId`, `router_id` and `ROUTER_ID` all end
 * up as "Router ID". Kept deliberately short: a word listed here can never be rendered
 * as ordinary prose again, so `id`, `ip` and friends earn their place while anything
 * ambiguous does not. */
const ACRONYMS = new Set([
    'id',
    'ip',
    'url',
    'uri',
    'cidr',
    'dns',
    'api',
    'tcp',
    'udp',
    'http',
    'https',
    'ssl',
    'tls',
    'vpn',
    'mtu',
    'asn',
    'cpu',
    'os',
    'sa',
    'ha',
]);

/**
 * Alert detail panel - every field of one alert, as a table.
 *
 * The right-hand third of the alerts row, a card in its own right beside the list panel,
 * with chrome matching the dial-logs drawer (`libs/feature/dial-logs-timeline`) - though
 * it takes an @Input rather than earning a global service and a shell host, because it has
 * exactly one caller. Every field is rendered rather than a chosen few: this is the view somebody
 * opens when the row did not answer their question, so the interesting field is the one a
 * summary would have dropped.
 */
@Component({
    selector: 'app-alert-detail-drawer',
    templateUrl: './alert-detail-drawer.component.html',
    styleUrls: ['./alert-detail-drawer.component.scss'],
    standalone: false,
})
export class AlertDetailDrawerComponent implements OnChanges {
    @Input() alert?: AlertView;
    /**
     * The mute silencing this alert, when the page has it.
     *
     * Passed in rather than read here: the alert carries only `muted.by`, and the mute
     * behind it arrives with the listing under `_embedded.muteList`, so the page already
     * holds it. Absent whenever the alert is not muted, and absent on a muted alert whose
     * mute the page has not got - the Muting block then shows the mute id instead.
     */
    @Input() mute?: Mute;
    /** Position within the list the drawer was opened from, for the header pager. */
    @Input() index = -1;
    @Input() total = 0;
    /**
     * When true, the drawer shows a triage footer (Claim/Release, Resolve, Mute) that acts on the
     * alert in place. Off by default so the Alerts page — whose rows already carry these actions —
     * is unchanged; the dashboard's shell-hosted drawer turns it on.
     */
    @Input() showActions = false;
    /** Whether the footer links to the network Alerts page. */
    @Input() showOpenInAlerts = true;

    @Output() closed = new EventEmitter<void>();
    @Output() navigated = new EventEmitter<-1 | 1>();
    /**
     * Asks the page to re-read the alert.
     *
     * An output rather than a fetch of its own, because the drawer does not own the
     * alert: it renders the row the table holds. Reading one here would leave the drawer
     * describing an alert the row behind it no longer matches, and it would miss the
     * mute — which arrives embedded with the listing, not with a single alert.
     */
    @Output() refreshed = new EventEmitter<void>();

    /* Details first: it is what the drawer is for, and History costs a request. */
    tab: DrawerTab = 'details';
    /** The history of the alert on screen, or undefined until the tab is first opened. */
    timeline?: Timeline;
    loadingHistory = false;
    /* Why the history could not be read, or ''. Held on the tab rather than raised as a
     * growler: it describes the one thing that tab shows, and a growler would outlive the
     * view that explains it. */
    historyError = '';

    /** The latency policy that graded the alert on screen, or undefined until the tab is opened. */
    policy?: LatencyPolicy;
    /** The offline policy that graded the alert on screen, or undefined until the tab is opened. */
    offlinePolicy?: OfflinePolicy;
    loadingPolicy = false;
    /** Why the policy could not be read, or ''. On the tab, for the same reason. */
    policyError = '';

    /* The alert the two read panes belong to. The drawer is reused as the reader pages
     * through the table, so this is what stops alert A's timeline being shown under
     * alert B while B's is still in flight — and what stops a poll, which replaces the
     * row object without changing the alert, dropping what is already on screen. */
    private shownAlertId = '';
    /* Bumped on every read. A response for a generation that is no longer current has
     * been superseded — by paging to another alert, or by a reload — and is dropped
     * rather than painted over what the reader is now looking at. */
    private historyGeneration = 0;
    private policyGeneration = 0;

    /** True while a triage action is in flight, so the footer buttons go inert. */
    saving = false;
    /** The full Alerts page, for the footer's "Open in Alerts" deep link. */
    readonly alertsPageRoute = URLS.ALERTS;

    constructor(
        private alertService: AlertServiceV2,
        private authorizationService: AuthorizationService,
        private logger: LoggerService,
        private dialog: MatDialog,
        private growler: GrowlerService,
        public assistant: AssistantChatService,
        private alertTypes: AlertTypeRegistry
    ) {}

    /**
     * Follows the alert, because the drawer is reused rather than rebuilt as the reader
     * pages through the table.
     *
     * The tab choice survives the move, but neither read pane does: both are dropped and
     * re-read, or the second alert would show the first one's story. A pane the next alert
     * does not offer is the exception - Policy is not on every alert, so the reader goes
     * back to Details, which every alert has.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['alert']) {
            return;
        }
        const id = this.alert?.id || '';
        if (id === this.shownAlertId) {
            return;
        }
        this.shownAlertId = id;
        this.timeline = undefined;
        this.historyError = '';
        this.policy = undefined;
        this.offlinePolicy = undefined;
        this.policyError = '';
        if (!this.tabs.some((pane) => pane.id === this.tab)) {
            this.tab = 'details';
        }
        if (this.tab === 'history') {
            this.loadHistory();
        }
        if (this.tab === 'policy') {
            this.loadPolicy();
        }
    }

    /**
     * The panes on offer for the alert on screen.
     *
     * Two come and go, both because there is nothing behind them: Policy is dropped for an
     * alert that names no policy and for a reader who may not read policies, and Muting is
     * dropped for an alert no mute is in force on - see {@link isMuted}. Dropped rather than
     * shown empty or disabled, because a tab that opens onto "there is no policy" is a click
     * that answers nothing, and a reader on a pane the next alert does not offer lands back
     * on Details - see {@link ngOnChanges}.
     */
    get tabs(): { id: DrawerTab; label: string }[] {
        return DRAWER_TABS.filter((pane) => {
            if (pane.id === 'policy') {
                return this.hasPolicy;
            }
            if (pane.id === 'muting') {
                return this.isMuted;
            }
            return true;
        });
    }

    /**
     * Whether a mute is in force on the alert on screen.
     *
     * Computed per request from the mutes in force when the alert was read, so this can
     * differ between two loads with nothing written to the alert. The flag alone: a mute
     * that has lapsed or been revoked is not in force, so the pane explaining it is not
     * offered, and the History pane is where a mute that has since gone leaves its mark.
     */
    get isMuted(): boolean {
        return this.alert?.muted?.muted === true;
    }

    // ---- Details tab presentation ----------------------------------------------------------------
    // The Details pane is drawn as a purpose-built triage view (summary band + component + ownership)
    // rather than the generic key/value table the other panes use, so these expose the alert in the
    // shape that view needs. Severity and status keys drive the tone classes in the template.

    /** CRITICAL | MAJOR | MINOR | INFO — the tone class key for the summary band. */
    get severityKey(): string {
        return this.alert?.severity || '';
    }

    /** "MAJOR" -> "Major" for display. */
    get severityLabel(): string {
        const s = this.alert?.severity || '';
        return s ? s.charAt(0) + s.slice(1).toLowerCase() : '';
    }

    /** FIRING | PENDING | RESOLVED — drives the status dot tone. */
    get statusKey(): string {
        return this.alert?.status || '';
    }

    /** "Firing" | "Pending" | "Resolved" — the status cell value. */
    get statusLabel(): string {
        const s = this.alert?.status || '';
        return s ? s.charAt(0) + s.slice(1).toLowerCase() : '';
    }

    /** How long the alert has been in its current state, e.g. "26m". */
    get ageText(): string {
        const iso = this.alert?.status === 'RESOLVED' ? this.alert?.resolvedAt : this.alert?.firstAssertedAt;
        return this.sinceText(iso) || '—';
    }

    /** "2 routers" / "1 service" / "3 components" — the scope cell value. */
    get scopeText(): string {
        const comps = this.detailComponents;
        const count = comps.length;
        if (!count) {
            return '—';
        }
        const kinds = new Set(comps.map((c) => c.kind));
        const noun = kinds.size === 1 ? this.scopeNoun([...kinds][0]) : 'component';
        return `${count} ${noun}${count === 1 ? '' : 's'}`;
    }

    private scopeNoun(kind: string): string {
        switch (kind) {
            case 'routers':
                return 'router';
            case 'services':
                return 'service';
            case 'link':
                return 'link';
            case 'detector':
                return 'detector';
            default:
                return 'component';
        }
    }

    /** The detector stopped asserting the condition, but the alert has not resolved. */
    get isStale(): boolean {
        return this.alert?.stale === true;
    }

    /**
     * The affected components, shaped for the readout list. `endLetter` is the A/B suffix
     * `componentPartLabels` gives repeated types (a link's two edge routers), '' otherwise.
     */
    get detailComponents(): { name: string; id: string; kind: string; iconClass: string; endLetter: string; route: any[] | null }[] {
        const alert = this.alert;
        if (!alert || (!alert.componentType && !alert.componentId && isEmpty(alert.components))) {
            return [];
        }
        const parts = this.componentParts(alert);
        const labels = componentPartLabels(parts.map((part) => part.componentType));
        return parts.map((entry, index) => {
            const name = this.text(entry.componentName);
            const letter = /\s([A-Z])$/.exec(labels[index] || '');
            const kind = this.componentKind(entry.componentType);
            const id = entry.componentId || '';
            return {
                name: isEmpty(name) ? UNNAMED_COMPONENT : name,
                id,
                kind,
                iconClass: this.componentIcon(entry.componentType),
                endLetter: letter ? letter[1] : '',
                route: this.componentRoute(kind, id),
            };
        });
    }

    /** Deep-link to a component's detail page, or null where there is no single page to open
     * (a link spans two routers, a detector has no page, and an unnamed part has no id). */
    private componentRoute(kind: string, id: string): any[] | null {
        if (isEmpty(id)) {
            return null;
        }
        switch (kind) {
            case 'routers':
                return ['/routers', id];
            case 'services':
                return ['/services/advanced', id];
            default:
                return null;
        }
    }

    /** Who holds the alert (name, else email), for the ownership block. */
    get claimedByName(): string {
        return this.text(this.alert?.claimedBy?.name) || this.text(this.alert?.claimedBy?.email) || 'Someone';
    }

    /** How long ago the alert was claimed, e.g. "5m ago". */
    get claimedAgo(): string {
        const at = this.sinceText(this.alert?.claim?.at);
        return at ? `${at} ago` : '';
    }

    /** The note left with the claim, or ''. */
    get claimNote(): string {
        return this.text(this.alert?.claim?.note);
    }

    /** Resource-family key for a component type, for the chip tone. */
    private componentKind(type?: string): string {
        switch (type) {
            case 'SERVICE':
                return 'services';
            case 'EDGE_ROUTER':
                return 'routers';
            case 'LINK':
                return 'link';
            case 'DETECTOR':
                return 'detector';
            default:
                return 'other';
        }
    }

    /** Icon-font class for a component type's chip. */
    private componentIcon(type?: string): string {
        switch (type) {
            case 'SERVICE':
                return 'icon-services';
            case 'EDGE_ROUTER':
            case 'LINK':
                return 'icon-routers';
            case 'DETECTOR':
                return 'icon-Bell';
            default:
                return 'icon-Policies';
        }
    }

    /** A compact "how long since" string: "<1m", "6m", "3h", "2d". */
    private sinceText(iso?: string): string {
        if (!iso) {
            return '';
        }
        const then = Date.parse(iso);
        if (Number.isNaN(then)) {
            return '';
        }
        const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
        if (mins < 1) {
            return '<1m';
        }
        if (mins < 60) {
            return `${mins}m`;
        }
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) {
            return `${hrs}h`;
        }
        return `${Math.floor(hrs / 24)}d`;
    }

    get hasPrev(): boolean {
        return this.index > 0;
    }

    get hasNext(): boolean {
        return this.index >= 0 && this.index < this.total - 1;
    }

    /** Open the AI assistant and ask it to explain this alert. */
    explainWithAssistant(): void {
        if (this.alert) {
            this.assistant.ask(explainAlertPrompt(this.alert));
        }
    }

    get title(): string {
        return this.alert ? alertTypeLabel(this.alert.alertType) : 'Alert';
    }

    /**
     * The raw alert type, shown beside the readable one in the header.
     *
     * The readable name is derived from this, and the two are not interchangeable: a mute
     * names the id, and a support ticket has to quote it. Keeping it in the header means
     * a reader can copy it without leaving the pane they are on.
     */
    get alertTypeId(): string {
        return this.alert?.alertType || '';
    }

    /**
     * The sections of the pane the reader is on.
     *
     * Four panes over one alert, so no single list has to be both the summary somebody
     * scans during an incident and the full document somebody quotes into a ticket - the
     * split is by question rather than by field type. Empty sections are dropped here
     * rather than in each builder, so a pane whose sections are all empty renders as
     * nothing rather than as a row of bare headings.
     */
    get sections(): DetailSection[] {
        const alert = this.alert;
        if (!alert) {
            return [];
        }
        const sections = this.sectionsFor(this.tab, alert);
        return sections.filter((section) => section.rows.length > 0);
    }

    /**
     * Every section of every pane, for a caller that has to see the whole document.
     *
     * Exists so "is any field of the alert unreachable" is one question with one answer,
     * rather than four that have to be kept in step by hand.
     */
    get allSections(): DetailSection[] {
        const alert = this.alert;
        if (!alert) {
            return [];
        }
        return DETAIL_TABS.flatMap((tab) => this.sectionsFor(tab, alert)).filter((section) => section.rows.length > 0);
    }

    private sectionsFor(tab: DrawerTab, alert: AlertView): DetailSection[] {
        if (tab === 'source') {
            return this.sourceSections(alert);
        }
        if (tab === 'policy') {
            return this.policySections(alert);
        }
        if (tab === 'muting') {
            return this.mutingSections(alert);
        }
        if (tab === 'advanced') {
            return this.advancedSections(alert);
        }
        return this.detailSections(alert);
    }

    /** What is wrong, what it is about, and who has it. */
    private detailSections(alert: AlertView): DetailSection[] {
        return [
            {
                key: 'status',
                title: 'Status',
                rows: [
                    /* No alert type row: the header carries the readable name and the
                     * raw id, above every pane. */
                    { key: 'status', label: 'Status', value: this.text(alert.status) },
                    { key: 'severity', label: 'Severity', value: this.text(alert.severity) },
                    { key: 'open', label: 'Still open', value: this.bool(alert.open) },
                    /* Stale means the detector stopped asserting the condition. It is
                     * not the same as resolved, and the difference matters. */
                    { key: 'stale', label: 'No longer seen', value: this.bool(alert.stale) },
                ],
            },
            /* Its own block rather than three more rows in Identity: what the alert is
             * about is the first thing a reader looks for, and a heading is what makes it
             * findable at a glance on a narrow pane.
             *
             * The name is not stored on the alert - it is resolved per request - so it is
             * absent whenever no controller could answer, and the id below it is then the
             * only handle the reader has. Both are shown rather than one collapsing into
             * the other, because they are different facts: the id identifies the
             * component for as long as the alert lives, the name only describes it now. */
            {
                key: 'component',
                title: 'Component',
                /* Empty when the alert is about no component - a detector health alert, for
                 * instance - and an empty section is dropped below. Three dashes under a
                 * "Component" heading would say the lookup failed, when in fact there was
                 * nothing to look up. */
                rows: this.componentRows(alert),
            },
            {
                key: 'ownership',
                title: 'Ownership',
                /* Empty when nobody holds the alert, and an empty section is dropped below.
                 * A block reading "Claimed: No" over four blank rows spends a quarter of the
                 * pane saying nothing happened — and the row buttons already offer Claim,
                 * which is the same fact as an offer to act on. */
                rows: this.ownershipRows(alert),
            },
        ];
    }

    /**
     * Who holds the alert, or nothing at all.
     *
     * Empty for an alert nobody has claimed, so the section is dropped rather than drawn
     * saying so - what a reader does about an unclaimed alert is claim it, and the row
     * offers that already. Any sign of a claim is enough to draw it, because the person,
     * the time and the note are resolved and stored separately from the flag.
     */
    private ownershipRows(alert: AlertView): DetailRow[] {
        if (!this.isClaimed(alert)) {
            return [];
        }
        return [
            /* Ownership, not silence: a claimed alert still fires, still expires and still
             * resolves on its own. That is why it sits here and muting has a pane of its
             * own — they answer different questions. */
            { key: 'claimed', label: 'Claimed', value: this.bool(alert.claim?.claimed) },
            /* Name and email, both resolved per request from the identity service. Not the
             * identity id stored behind them: nothing on these panes is acted on by a
             * person's id, and a reader looking for who holds an alert is looking for a
             * person rather than a key. */
            { key: 'claimedBy', label: 'By', value: this.text(alert.claimedBy?.name) },
            { key: 'claimedByEmail', label: 'Email', value: this.text(alert.claimedBy?.email) },
            { key: 'claimedAt', label: 'At', value: this.time(alert.claim?.at) },
            { key: 'claimNote', label: 'Note', value: this.text(alert.claim?.note) },
        ];
    }

    /** Whether anything on the alert says somebody holds it. See {@link ownershipRows}. */
    private isClaimed(alert: AlertView): boolean {
        return (
            alert.claim?.claimed === true ||
            !isEmpty(alert.claimedBy?.name) ||
            !isEmpty(alert.claimedBy?.email) ||
            !isNil(alert.claim?.at) ||
            !isEmpty(alert.claim?.note)
        );
    }

    /**
     * Where the alert came from, what the detector saw, and when it happened.
     *
     * Evidence sits with the source rather than with the summary because it is the
     * detector's own working: it is free-form, it varies per detector, and it is read
     * when somebody is asking why this fired rather than what fired.
     */
    private sourceSections(alert: AlertView): DetailSection[] {
        return [
            {
                key: 'source',
                title: 'Source',
                rows: [
                    { key: 'detectorId', label: 'Detected by', value: this.text(alert.detectorId), mono: true },
                    /* Network and network group are deliberately absent: the page is
                     * already scoped to one network, so both are the same on every row
                     * and neither tells the reader anything the header does not. */
                    { key: 'sourceId', label: 'Came from', value: this.text(alert.sourceId), mono: true },
                ],
            },
            {
                key: 'evidence',
                title: 'Evidence',
                rows: this.fromRecord(alert.evidence, alert.components, alert.severity),
            },
            {
                key: 'timeline',
                title: 'Timeline',
                rows: [
                    /* The listing sorts on firstAssertedAt, not openedAt — openedAt is
                     * null while an alert is still pending. Both are shown so the gap
                     * between them (the `for` hold) is visible. */
                    { key: 'firstSeen', label: 'First seen', value: this.time(alert.firstAssertedAt) },
                    { key: 'openedAt', label: 'Opened', value: this.time(alert.openedAt) },
                    { key: 'lastSeen', label: 'Last seen', value: this.time(alert.lastAssertedAt) },
                    { key: 'expiresAt', label: 'Expires', value: this.time(alert.expiresAt) },
                    { key: 'resolvedAt', label: 'Resolved', value: this.time(alert.resolvedAt) },
                    { key: 'resolutionReason', label: 'Why it resolved', value: this.text(alert.resolutionReason) },
                ],
            },
        ];
    }

    /**
     * The ladder this alert was graded against.
     *
     * Read from the service rather than derived, so this pane is empty until the policy
     * arrives and the template shows the loading or error note instead. `alert` is used
     * only to name the policy's components, because the alert carries which policy graded
     * it and nothing about what that policy holds.
     */
    private policySections(alert: AlertView): DetailSection[] {
        if (this.offlinePolicy) {
            return this.offlinePolicySections(this.offlinePolicy, alert);
        }
        const policy = this.policy;
        if (isNil(policy)) {
            return [];
        }
        return [
            {
                key: 'policy',
                title: 'Policy',
                rows: [
                    /* How far it reaches, first. It is the difference between a threshold
                     * that covers this component and one that covers every subject of the
                     * alert type, and it changes what the numbers below mean. */
                    { key: 'policyScope', label: 'Scope', value: policyScope(policy) },
                    { key: 'policyAlertType', label: 'Alert type', value: this.text(policy.alertType) },
                    { key: 'policyKind', label: 'Compares against', value: kindLabel(policyKind(policy)) },
                    ...this.policyComponentRows(policy, alert),
                ],
            },
            { key: 'thresholds', title: 'Thresholds', rows: this.thresholdRows(policy) },
            {
                key: 'changed',
                title: 'Changed',
                /* When a threshold moved and who moved it. The name only — the identity
                 * ids behind it are not shown, because nothing on this pane is acted on by
                 * id: the policy id is the handle a ticket quotes, and a person is looked
                 * up by name. */
                rows: this.changedRows(policy),
            },
        ];
    }

    /**
     * The offline policy that graded the alert: its scope, whether it alerts, and its ladder with the alert's severity
     * marked. A policy with no ladder of its own says so, because the next level's ladder applies.
     */
    private offlinePolicySections(policy: OfflinePolicy, alert: AlertView): DetailSection[] {
        const ladder: Record<string, string> = {};
        for (const rung of OFFLINE_RUNGS) {
            const value = policy.ladder?.[rung.key];
            if (!isEmpty((value || '').trim())) {
                ladder[rung.severity] = value as string;
            }
        }
        let ladderRow: DetailRow;
        if (!policy.enabled) {
            ladderRow = { key: 'ladder', label: 'Ladder', value: 'None. This policy switches offline alerts off.' };
        } else if (isEmpty(ladder)) {
            ladderRow = {
                key: 'ladder',
                label: 'Ladder',
                value: 'Not set. The network policy or the platform default applies.',
            };
        } else {
            ladderRow = {
                key: 'ladder',
                label: 'Ladder',
                value: ladderSummary(policy.ladder),
                table: this.ladderTable(ladder, alert.severity),
            };
        }
        return [
            {
                key: 'policy',
                title: 'Policy',
                rows: [
                    { key: 'policyScope', label: 'Scope', value: offlinePolicyScope(policy) },
                    ...(offlineComponentIds(policy).length > 0 ? this.policyComponentRows(policy, alert) : []),
                    { key: 'policyEnabled', label: 'Alerting', value: policy.enabled ? 'On' : 'Off' },
                    ladderRow,
                ],
            },
            { key: 'changed', title: 'Changed', rows: this.changedRows(policy) },
        ];
    }

    /** When a policy was created and last changed, and by whom. The update rows are blank on a policy nobody has edited. */
    private changedRows(policy: {
        createdAt?: string;
        createdBy?: Person | null;
        updatedAt?: string | null;
        updatedBy?: Person | null;
    }): DetailRow[] {
        return [
            { key: 'createdAt', label: 'Created', value: this.time(policy.createdAt) },
            { key: 'createdBy', label: 'Created by', value: this.text(policy.createdBy?.name) },
            { key: 'updatedAt', label: 'Updated', value: this.time(policy.updatedAt || undefined) },
            { key: 'updatedBy', label: 'Updated by', value: this.text(policy.updatedBy?.name) },
        ];
    }

    /**
     * Why nobody is being told, and what happened to the notification.
     *
     * The two belong together and nowhere else: a mute is the reason a notification did
     * not go out, and the notification block is the evidence that it did not. Split
     * across panes, a reader would have to hold one to make sense of the other.
     */
    private mutingSections(alert: AlertView): DetailSection[] {
        return [
            {
                key: 'muting',
                title: 'Muting',
                rows: [
                    /* Computed per request from the mutes in force at read time, so this
                     * can differ between two loads with nothing written to the alert. */
                    { key: 'muted', label: 'Muted', value: this.bool(alert.muted?.muted) },
                    /* Who silenced the alert, which is the answer to "why did nobody get
                     * paged". Prefixed where Ownership's rows are bare, because this
                     * section's own rows already are - beside `Mute id`, a plain `By`
                     * would read as the odd one out. The name and the email, not the
                     * identity id: nothing in this block is acted on by a person's id. */
                    { key: 'mutedBy', label: 'Muted by', value: this.text(this.mute?.createdBy?.name) },
                    { key: 'mutedByEmail', label: 'Muted by email', value: this.text(this.mute?.createdBy?.email) },
                    { key: 'mutedAt', label: 'Muted at', value: this.time(this.mute?.createdAt) },
                    { key: 'muteId', label: 'Mute id', value: this.text(alert.muted?.by), mono: true },
                    /* Off the alert, with the mute as the fallback: the alert carries the
                     * reason of the mute in force when it was read, and a service that
                     * sent one without the other must not blank the field. */
                    {
                        key: 'muteReason',
                        label: 'Mute reason',
                        value: this.text(alert.muted?.reason || this.mute?.reason),
                    },
                ],
            },
            {
                key: 'notification',
                title: 'Notification',
                rows: [
                    { key: 'notificationState', label: 'State', value: this.text(alert.notification?.state) },
                    { key: 'notificationSentAt', label: 'Sent', value: this.time(alert.notification?.sentAt) },
                    /* Not "Muted by": the Muting block above already uses that for the person,
                       and these two now share a pane. This one is a mute id and it
                       answers a different question — not who silenced the alert, but
                       which mute stopped this particular notification going out. */
                    {
                        key: 'notificationMutedBy',
                        label: 'Stopped by mute',
                        value: this.text(alert.notification?.mutedBy),
                        mono: true,
                    },
                ],
            },
        ];
    }

    /**
     * The raw material behind the other three panes.
     *
     * Identifiers a support ticket has to quote, and the detector's own free-form
     * dimensions. Neither answers a question somebody has during an incident, and both
     * are the first thing asked for afterwards — which is exactly why they are one click
     * away rather than pushing the readable fields off a narrow pane.
     */
    private advancedSections(alert: AlertView): DetailSection[] {
        return [
            {
                key: 'identifiers',
                title: 'Identifiers',
                rows: [
                    /* The raw id behind the readable name in the header: it is what a
                     * mute names, and what a support ticket has to quote. */
                    { key: 'alertTypeId', label: 'Alert type id', value: this.text(alert.alertType), mono: true },
                    { key: 'alertId', label: 'Alert id', value: this.text(alert.id), mono: true },
                    /* Two alerts with the same fingerprint are the same condition. This
                     * is the field that says so, and it is the one somebody reaches for
                     * when two alerts look identical and they need to know whether they
                     * are the same thing twice. */
                    { key: 'fingerprint', label: 'Fingerprint', value: this.text(alert.fingerprint), mono: true },
                    ...this.policyIdRows(),
                ],
            },
            {
                key: 'labels',
                title: 'Labels',
                rows: this.fromRecord(alert.labels),
            },
        ];
    }

    /**
     * The governing policy's id, or no row at all.
     *
     * Read from the alert rather than from the loaded policy, so it is here on the pane a
     * reader opens for the ids without a second request behind one row; the loaded policy
     * is the fallback for an alert that links to its policy but does not name the id.
     * Dropped rather than left blank when nothing names a policy, because an alert graded
     * on the shipped defaults has no policy to quote.
     */
    private policyIdRows(): DetailRow[] {
        const id = governingPolicyId(this.alert) || this.policy?.id || this.offlinePolicy?.id || '';
        if (isEmpty(id)) {
            return [];
        }
        return [{ key: 'policyId', label: 'Policy id', value: this.text(id), mono: true }];
    }

    /* Tabs and history ------------------------------------------------------- */

    /**
     * Switches pane, reading the history the first time somebody asks for it.
     *
     * Lazy on purpose: the drawer opens on Details, and History is a second request that
     * nobody has asked for until they click. Once read it is held for as long as the
     * drawer stays on this alert, so switching back and forth is free.
     */
    setTab(tab: DrawerTab): void {
        if (this.tab === tab) {
            return;
        }
        this.tab = tab;
        if (tab === 'history' && !this.timeline && !this.loadingHistory) {
            this.loadHistory();
        }
        if (tab === 'policy' && !this.policy && !this.offlinePolicy && !this.loadingPolicy) {
            this.loadPolicy();
        }
    }

    /**
     * Re-reads whatever the open tab is showing.
     *
     * One button, three meanings, because the reader's intent is the same every time: Details
     * goes back to the page, since the drawer renders the row the table holds, while History
     * and Policy are the drawer's own and are re-read here. There is no 60s tick behind any
     * of them, which is why this button exists - a policy is the pane most likely to be
     * stale, because moving a threshold writes nothing to the alert.
     */
    refresh(): void {
        if (this.tab === 'history') {
            this.loadHistory();
            return;
        }
        if (this.tab === 'policy') {
            this.loadPolicy();
            return;
        }
        this.refreshed.emit();
    }

    /** What the reload button says it will re-read, which is whatever the open pane shows. */
    get refreshTitle(): string {
        if (this.tab === 'history') {
            return 'Reload the history of this alert';
        }
        if (this.tab === 'policy') {
            return 'Reload the policy this alert is measured against';
        }
        return 'Reload this alert';
    }

    /** True while the reload button should be inert: its own tab is already loading. */
    get refreshing(): boolean {
        return (this.tab === 'history' && this.loadingHistory) || (this.tab === 'policy' && this.loadingPolicy);
    }

    /**
     * Whether the Policy pane is on offer for the alert on screen.
     *
     * Two conditions, both about whether there is anything to show: the alert has to name a
     * policy, and the reader has to be allowed to read policies. The client check is not
     * the enforcement - the service answers 404 for a policy the caller may not see, so
     * this only keeps a reader who will always be refused from being offered the click.
     */
    get hasPolicy(): boolean {
        if (!hasGoverningPolicy(this.alert)) {
            return false;
        }
        return this.governingKind === 'offline'
            ? this.authorizationService.canListOfflinePolicies()
            : this.authorizationService.canListLatencyPolicies();
    }

    /** Which kind of policy graded the alert on screen. The alert type decides only when the alert carries no link. */
    get governingKind(): GoverningPolicyKind {
        return governingPolicyKind(this.alert, (alertType) =>
            this.alertTypes.offlinePolicyTypes.some((type) => type.alertType === alertType)
        );
    }

    /** What the Policy pane says before the policy, which differs between thresholds and an offline ladder. */
    get policyNote(): string {
        return this.governingKind === 'offline'
            ? 'The policy this component is graded against now. Changing it changes when the next offline alert fires, not this alert.'
            : 'The policy this alert type is measured against now. Changing these thresholds changes what the next measurement is compared with, not this alert.';
    }

    /** The events, newest first - which is the order the endpoint serves. */
    get events(): AlertEventView[] {
        return this.timeline?.events || [];
    }

    /**
     * True when the timeline was reconstructed rather than recorded.
     *
     * Surfaced rather than rendered silently, because a derived timeline is strictly less
     * than a recorded one - no severity changes, no freezes, no releases. An alert somebody
     * took and handed back looks here like one nobody touched.
     */
    get derived(): boolean {
        return this.timeline?.derived === true;
    }

    /** What happened, in words. An unknown type passes through rather than vanishing. */
    eventText(event: AlertEventView): string {
        return EVENT_TEXT[event.type] || event.type;
    }

    /**
     * Who did it.
     *
     * `performedBy` is null for two different reasons — a detector or the sweeper did it,
     * or the identity could not be resolved — and only `actor` tells them apart. So the
     * id is the fallback rather than a guess at which case this is, and an event with
     * neither says nothing rather than naming nobody.
     */
    eventActor(event: AlertEventView): string {
        const person = event.performedBy;
        return person?.name || person?.email || event.actor || '';
    }

    /**
     * The same for a table cell.
     *
     * Kept as its own method rather than folded into {@link eventActor} so the template
     * says which of the two it wants: an actor with nobody to name is blank in the table,
     * and that is a decision about the cell rather than about the event.
     */
    eventActorCell(event: AlertEventView): string {
        return this.eventActor(event) || EMPTY;
    }

    /** As above, for the detail column. */
    eventDetailCell(event: AlertEventView): string {
        return this.eventDetail(event) || EMPTY;
    }

    /**
     * What came with the event, in words.
     *
     * `detail` is free-form, so there are two cases: a `note` or a `reason` is prose
     * somebody wrote and is shown as written, and anything else is read the way the Details
     * pane reads `labels` and `evidence`. Nothing is dropped for not being recognised - a
     * nested value falls back to JSON rather than vanishing, and this pane has no table rule
     * of its own, because a `detail` map is a handful of scalars describing one
     * transition.
     */
    eventDetail(event: AlertEventView): string {
        const detail = event.detail;
        if (isNil(detail) || Object.keys(detail).length === 0) {
            return '';
        }
        const note = detail['note'] || detail['reason'];
        if (typeof note === 'string' && note !== '') {
            return note;
        }
        return Object.entries(detail)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, value]) => {
                const text = typeof value === 'object' && value !== null ? JSON.stringify(value) : `${value}`;
                return `${this.humanize(key)}: ${text}`;
            })
            .join(', ');
    }

    /** An event's timestamp in the reader's locale, the same as every other time here. */
    eventTime(event: AlertEventView): string {
        return this.time(event.at);
    }

    /**
     * Reads the history of the alert on screen.
     *
     * Never rejects and never raises a growler. Failure is reported on the tab, because
     * that is the only thing it affects: the Details pane beside it is already correct
     * and the table behind it is untouched.
     */
    private loadHistory(): void {
        const alertId = this.alert?.id;
        if (isNil(alertId) || alertId === '') {
            this.timeline = undefined;
            this.historyError = '';
            return;
        }
        const generation = ++this.historyGeneration;
        this.loadingHistory = true;
        this.historyError = '';
        this.alertService
            .getAlertHistory(alertId)
            .then((timeline) => {
                if (generation !== this.historyGeneration) {
                    return;
                }
                /* Taken as served. The endpoint answers newest first by default, which is
                 * the order this tab wants. The cap follows `sort`, so a timeline past 500
                 * entries comes back as the newest 500, nothing in the response says
                 * whether it was cut, and narrowing with `type` or `at` is how the contract
                 * says to read one that long. */
                this.timeline = timeline;
            })
            .catch((error) => {
                if (generation !== this.historyGeneration) {
                    return;
                }
                this.timeline = undefined;
                this.historyError = 'The history of this alert could not be loaded.';
                this.logger.error(`Failed to read the history of alert ${alertId}`, error);
            })
            .finally(() => {
                if (generation === this.historyGeneration) {
                    this.loadingHistory = false;
                }
            });
    }

    /**
     * Reads the policy the alert says graded it.
     *
     * Followed by the link the alert carried, falling back to the id when no link was
     * served, because the service decides where its own resources live. Never rejects and
     * never raises a growler, exactly as the history read does not - a 404 is named on its
     * own, because it is the one failure with a cause the reader can act on.
     */
    private loadPolicy(): void {
        const href = governingPolicyHref(this.alert);
        const id = governingPolicyId(this.alert);
        if (isEmpty(href) && isEmpty(id)) {
            this.policy = undefined;
            this.offlinePolicy = undefined;
            this.policyError = '';
            return;
        }
        const generation = ++this.policyGeneration;
        this.loadingPolicy = true;
        this.policyError = '';
        const offline = this.governingKind === 'offline';
        const read: Promise<void> = offline
            ? (isEmpty(href)
                  ? this.alertService.getOfflinePolicy(id)
                  : this.alertService.getOfflinePolicyByHref(href)
              ).then((policy) => {
                  if (generation === this.policyGeneration) {
                      this.offlinePolicy = policy;
                  }
              })
            : (isEmpty(href) ? this.alertService.getLatencyPolicy(id) : this.alertService.getLatencyPolicyByHref(href)).then(
                  (policy) => {
                      if (generation === this.policyGeneration) {
                          this.policy = policy;
                      }
                  }
              );
        read
            .catch((error) => {
                if (generation !== this.policyGeneration) {
                    return;
                }
                this.policy = undefined;
                this.offlinePolicy = undefined;
                if (error?.status === 404) {
                    this.policyError = offline
                        ? 'This policy no longer exists. The next level of offline policy applies now.'
                        : 'This policy no longer exists. Something else measures this alert type now.';
                } else {
                    this.policyError = 'The policy this alert was measured against could not be loaded.';
                }
                this.logger.error(
                    `Failed to read the ${offline ? 'offline' : 'latency'} policy that graded alert ${this.alert?.id}`,
                    error
                );
            })
            .finally(() => {
                if (generation === this.policyGeneration) {
                    this.loadingPolicy = false;
                }
            });
    }

    navigate(direction: -1 | 1): void {
        this.navigated.emit(direction);
    }

    close(): void {
        this.closed.emit();
    }

    // ---- Triage actions (footer, shown only when showActions) ----------------------------------
    // The same operations the Alerts page rows offer, gated the same way. After each write the
    // drawer does not own the alert, so it emits `refreshed` and the host re-reads — the alert's
    // muted/claim state is recomputed server-side and a hand-patched copy would be stale.

    /** True while the alert is claimed by someone (reuses the fuller ownership check). */
    get alertClaimed(): boolean {
        return !!this.alert && this.isClaimed(this.alert);
    }

    /** Claim is offered on an open, unclaimed alert; a resolved alert has no next to act on. */
    get canClaim(): boolean {
        return !!this.alert && !this.alertClaimed && this.alert.status !== 'RESOLVED';
    }

    /** Resolve is offered while the alert is still open. */
    get canResolve(): boolean {
        return this.alert?.open === true;
    }

    /** Mute is offered on an unmuted alert to a reader allowed to create mutes. */
    get canMuteAlert(): boolean {
        return !!this.alert && !this.alert.muted?.muted && this.authorizationService.canCreateMutes();
    }

    claimAction(): void {
        const alert = this.alert;
        if (!alert || this.saving) {
            return;
        }
        const data: ClaimAlertDialogData = { alert };
        this.dialog
            .open(ClaimAlertDialogComponent, { data, width: '560px', autoFocus: false })
            .afterClosed()
            .subscribe((request: ClaimRequest | undefined) => {
                if (request) {
                    this.runAction(() => this.alertService.claim(alert.id, request));
                }
            });
    }

    releaseAction(): void {
        const alert = this.alert;
        if (alert) {
            this.runAction(() => this.alertService.release(alert.id));
        }
    }

    resolveAction(): void {
        const alert = this.alert;
        if (alert) {
            this.runAction(() => this.alertService.resolve(alert.id));
        }
    }

    muteAction(): void {
        const alert = this.alert;
        if (!alert || this.saving) {
            return;
        }
        const data: MuteAlertDialogData = { alert };
        this.dialog
            .open(MuteAlertDialogComponent, { data, width: '560px', autoFocus: false })
            .afterClosed()
            .subscribe((request: MuteRequest | undefined) => {
                if (!request) {
                    return;
                }
                this.saving = true;
                this.alertService
                    .createMute(alert.networkId, request)
                    .then(() => {
                        this.growler.show(
                            new GrowlerData(
                                'success',
                                'Muted',
                                'Notifications stopped for the alerts this mute covers.',
                                'The alerts themselves keep firing and resolving as they did before.'
                            )
                        );
                        this.refreshed.emit();
                    })
                    .catch((error) => {
                        this.logger.error('Failed to create the mute', error);
                        this.growler.show(new GrowlerData('error', 'Failed to create the mute', '', ''));
                    })
                    .finally(() => {
                        this.saving = false;
                    });
            });
    }

    private runAction(action: () => Promise<AlertView>): void {
        if (this.saving) {
            return;
        }
        this.saving = true;
        action()
            .then(() => this.refreshed.emit())
            .catch((error) => {
                this.logger.error('That action failed', error);
                this.growler.show(new GrowlerData('error', 'That action failed', '', ''));
            })
            .finally(() => {
                this.saving = false;
            });
    }

    /**
     * The component block, or nothing at all.
     *
     * `Name` is kept even when it is absent, because on an alert that <em>does</em> have a
     * component its absence is information: no controller could answer, and the reader
     * should see that rather than wonder whether the drawer forgot to show it.
     */
    private componentRows(alert: AlertView): DetailRow[] {
        if (!alert.componentType && !alert.componentId) {
            return [];
        }
        const parts = this.componentParts(alert);
        return [
            { key: 'componentType', label: 'Type', value: this.componentTypeLabel(alert.componentType) },
            /* **No `Id` row and no `Name` row.** One row per component instead, whether the
             * subject is one thing or several: each carries the name with the id it belongs
             * to, so the two facts stay together on the line that shows them. The joined key
             * a policy is keyed by — `id1|id2` — is not a handle anybody reads, and both
             * halves of it are on the rows below. */
            ...parts.map((entry, index) => this.namedComponentRow(entry, index, parts)),
        ];
    }

    /**
     * The components the subject is made of.
     *
     * What the service listed, and the one the alert names itself where it listed nothing —
     * a subject of one component is the same thing as a list of one, and treating it as one
     * is what lets every alert render its components the same way.
     */
    private componentParts(alert: AlertView): NamedComponent[] {
        const named = alert.components || [];
        if (!isEmpty(named)) {
            return named;
        }
        return [
            {
                componentType: alert.componentType,
                componentId: alert.componentId,
                componentName: alert.componentName,
            } as NamedComponent,
        ];
    }

    /**
     * One of the components a subject is made of.
     *
     * Lettered where the kind repeats - `Edge router A`, `Edge router B` - by the same
     * {@link componentPartLabels} the policy form labels its id boxes with, so the two
     * halves are named the same way in both places. The name and the id are different
     * facts and the row shows both, with {@link UNNAMED_COMPONENT} standing in for a name
     * no controller could answer for.
     */
    private namedComponentRow(entry: NamedComponent, index: number, all: NamedComponent[]): DetailRow {
        const labels = componentPartLabels(all.map((part) => part.componentType));
        const label = labels[index] || this.componentTypeLabel(entry.componentType) || 'Component';
        const name = this.text(entry.componentName);
        return {
            key: `component${index}`,
            label,
            value: isEmpty(name) ? UNNAMED_COMPONENT : name,
            id: entry.componentId,
        };
    }

    /**
     * The components a policy overrides for, or nothing at all for a network policy.
     *
     * Read from `componentIds`, never by splitting `componentId`: the stored key is opaque
     * by contract, and the parts are served beside it so nothing has to guess at the
     * delimiter. Rendered as a table so each id can carry the name the alert resolved for
     * it, the same pairing an evidence row gets.
     */
    private policyComponentRows(
        policy: Pick<LatencyPolicy, 'componentIds' | 'componentId' | 'componentType'>,
        alert: AlertView
    ): DetailRow[] {
        const parts = policy.componentIds || [];
        const ids = parts.length > 0 ? parts : isEmpty(policy.componentId) ? [] : [policy.componentId as string];
        if (ids.length === 0) {
            return [];
        }
        const names = new Map(
            (alert.components || []).map((component) => [component.componentId, component.componentName])
        );
        return [
            {
                key: 'policyComponents',
                label: ids.length > 1 ? 'Components' : 'Component',
                value: ids.join(', '),
                table: {
                    variant: 'records',
                    columns: [this.componentTypeLabel(policy.componentType || undefined) || 'Component'],
                    rows: ids.map((id) => [this.cell(COMPONENT_ID_KEY, id, names)]),
                },
            },
        ];
    }

    /**
     * The ladder, in whichever units the policy's kind is measured in.
     *
     * An em dash rather than a blank for a rung nobody set, which is the one place this
     * drawer departs from rendering an absent value as nothing - an absent rung is
     * unreachable rather than zero, and the Latency Policies page writes them the same way.
     * A policy with neither kind says so in a sentence, because the service will not store
     * one and whatever this covers is then graded by nothing.
     */
    private thresholdRows(policy: LatencyPolicy): DetailRow[] {
        const kind = policyKind(policy);
        if (kind === 'AUTO') {
            const auto = policy.auto;
            return [
                { key: 'minor', label: 'Minor', value: formatMultiplier(auto?.minorMultiplier) },
                { key: 'major', label: 'Major', value: formatMultiplier(auto?.majorMultiplier) },
                { key: 'critical', label: 'Critical', value: formatMultiplier(auto?.criticalMultiplier) },
                /* Both bounds are absolute, and both change what the multipliers above do:
                 * the floor is what stops a fast subject paging on a harmless jump, and
                 * the ceiling is always critical whatever the baseline says. */
                { key: 'floor', label: 'Floor', value: formatMillis(auto?.floorMillis) },
                { key: 'ceiling', label: 'Ceiling', value: formatMillis(auto?.ceilingMillis) },
            ];
        }
        if (kind === 'FIXED') {
            const fixed = policy.fixed;
            return [
                { key: 'minor', label: 'Minor', value: formatMillis(fixed?.minorMillis) },
                { key: 'major', label: 'Major', value: formatMillis(fixed?.majorMillis) },
                { key: 'critical', label: 'Critical', value: formatMillis(fixed?.criticalMillis) },
            ];
        }
        return [
            { key: 'noThresholds', label: 'Thresholds', value: 'This policy holds no thresholds, so it never alerts.' },
        ];
    }

    /** `EDGE_ROUTER` reads as `Edge router`. An unknown type is shown as sent rather than hidden. */
    private componentTypeLabel(type?: string): string {
        if (!type) {
            return EMPTY;
        }
        return COMPONENT_TYPE_LABELS[type] || type;
    }

    /**
     * Free-form maps rendered as rows, sorted by key so the same alert reads the same way
     * twice. Objects are stringified rather than dropped, because evidence carries whatever
     * a detector chose to attach. {@link DROPPED_KEYS} is the one exception and
     * {@link TRAILING_KEYS} the one departure from the alphabet.
     */
    private fromRecord(
        record: Record<string, any> | undefined,
        named?: NamedComponent[],
        severity?: AlertSeverity
    ): DetailRow[] {
        const source = record || {};
        // A single-cause breakdown makes the scalar summary redundant: `failureCauses: { X: n }` says
        // the same thing as `failureCause: X`, so the singular is dropped and the breakdown kept (it
        // also carries the count). With more than one cause the two differ — the singular is the
        // primary cause, the map the distribution across all of them — and both are shown.
        const causesSingle = isScalarMap(source['failureCauses']) && Object.keys(source['failureCauses']).length === 1;
        return (
            Object.entries(source)
                .filter(([key]) => !DROPPED_KEYS.has(key))
                .filter(([key]) => !(causesSingle && key === 'failureCause'))
                .sort(([a], [b]) => this.keyRank(a) - this.keyRank(b) || a.localeCompare(b))
                /* Sorted and rendered by the raw key, but shown by the readable one. */
                .map(([key, value]) => this.rowFor(key, value, named, severity))
        );
    }

    /**
     * One row, as a table where the value is a list of objects and as text everywhere else.
     *
     * `value` is populated either way, and for a table it holds the same JSON the cell used
     * to show. That is not a leftover: it is what the detector asserted and what somebody
     * pastes into a ticket, and anything reading rows as strings still gets the whole value
     * rather than an empty cell.
     */
    private rowFor(key: string, value: any, named?: NamedComponent[], severity?: AlertSeverity): DetailRow {
        const text = typeof value === 'object' && value !== null ? JSON.stringify(value) : this.text(value);

        if (isLadder(value)) {
            return { key, label: this.labelFor(key), value: text, table: this.ladderTable(value, severity) };
        }

        if (isRowList(value)) {
            return { key, label: this.labelFor(key), value: text, table: this.tableFrom(value, named) };
        }

        if (isScalarMap(value)) {
            return { key, label: this.labelFor(key), value: text, table: this.mapTable(value, named) };
        }

        /* Shown in the reader's own zone, and not monospaced: it is a sentence about a
         * moment rather than an identifier to compare character by character, which is
         * what every other row here is. The same rendering the Timeline block gives the
         * alert's own times, so the two read alike on one narrow pane. */
        if (isInstant(value)) {
            return { key, label: this.labelFor(key), value: this.time(value) };
        }

        /* And so does a flag, for the reason {@link cell} gives: Yes is what every other
         * block on these panes shows, and `false` here beside `No` there would be the same
         * fact written two ways on one alert. Not monospaced — it is a word, not an
         * identifier to compare character by character. */
        if (typeof value === 'boolean') {
            return { key, label: this.labelFor(key), value: this.bool(value) };
        }

        return { key, label: this.labelFor(key), value: text, mono: true };
    }

    /**
     * Columns are the union of the rows' keys, ranked by `COLUMN_ORDER` and first-seen
     * within a rank.
     *
     * A union rather than the first row's keys, so a row carrying an extra field widens the
     * table instead of losing the field - the same reason an unranked column is shown last
     * rather than dropped. First-seen inside a rank, because a detector writes the fields
     * of a row in the order they read, and the sort is stable.
     */
    private tableFrom(rows: Record<string, any>[], named?: NamedComponent[]): DetailTable {
        const columns: string[] = [];
        for (const row of rows) {
            for (const key of Object.keys(row)) {
                if (!columns.includes(key) && !DROPPED_COLUMN_KEYS.has(key)) {
                    columns.push(key);
                }
            }
        }
        columns.sort((a, b) => this.columnRank(a) - this.columnRank(b));

        const names = new Map((named || []).map((component) => [component.componentId, component.componentName]));

        return {
            variant: 'records',
            columns: columns.map((key) => COLUMN_LABELS[key] || this.labelFor(key)),
            rows: rows.map((row) => columns.map((key) => this.cell(key, row[key], names))),
        };
    }

    /**
     * A map of scalars as one row per entry, sorted by key so the same alert reads the same
     * way twice - the rule {@link fromRecord} follows one level up. The key is shown as the
     * detector wrote it and never humanised, because `NO_TERMINATORS` is an error code
     * somebody greps for.
     */
    private mapTable(map: Record<string, any>, named?: NamedComponent[]): DetailTable {
        const names = new Map((named || []).map((component) => [component.componentId, component.componentName]));
        return {
            variant: 'map',
            columns: [...MAP_COLUMNS],
            rows: Object.keys(map)
                .sort((a, b) => a.localeCompare(b))
                .map((key) => [{ value: key, mono: true }, this.cell(key, map[key], names)]),
        };
    }

    /**
     * A ladder as one row per rung, least severe first, so it reads down the escalation. The rung
     * that matches the alert's severity is marked as current. A threshold that is not an ISO-8601
     * duration is shown as sent.
     */
    private ladderTable(ladder: Record<string, any>, severity?: AlertSeverity): DetailTable {
        return {
            variant: 'ladder',
            columns: [...LADDER_COLUMNS],
            rows: SEVERITY_ORDER.filter((rung) => rung in ladder).map((rung) => {
                const current = rung === severity;
                return [
                    { value: rung.charAt(0) + rung.slice(1).toLowerCase(), severity: rung, current },
                    { value: formatIsoDuration(ladder[rung]) ?? this.text(ladder[rung]), current },
                ];
            }),
        };
    }

    /**
     * What one field is called, on a row and in a header alike.
     *
     * {@link FIELD_LABELS} first, then the humanised key. One place, so a name cannot come
     * out one way in a key/value row and another as a column of the same alert.
     */
    private labelFor(key: string): string {
        return FIELD_LABELS[key] || this.humanize(key);
    }

    /** Which group a free-form key sorts in: 0 for every key, 1 for a {@link TRAILING_KEYS} one. */
    private keyRank(key: string): number {
        return TRAILING_KEYS.has(key.toLowerCase()) ? 1 : 0;
    }

    /** Where a column sits: the first `COLUMN_ORDER` pattern it matches, or after all of them. */
    private columnRank(key: string): number {
        const rank = COLUMN_ORDER.findIndex((pattern) => pattern.test(key));
        return rank === -1 ? COLUMN_ORDER.length : rank;
    }

    /**
     * One cell, with one column read by name rather than as free text.
     *
     * `componentId` is shown as the component's name with the id underneath, because the id
     * on its own says nothing a reader can act on - and it is keyed on the column name, not
     * on the alert type, so any detector that names a component gets it with no change
     * here. The id is the fallback, never a blank, because a name is null whenever no
     * controller could answer.
     */
    private cell(key: string, value: any, names: Map<string, string | null>): DetailCell {
        if (key === COMPONENT_ID_KEY && typeof value === 'string') {
            const name = names.get(value);
            return isNil(name) ? { value, mono: true } : { value: name, id: value };
        }

        /* An instant reads the same wherever this drawer shows it, so a time in a table
         * row gets the reader's own zone exactly as a key/value row does. */
        if (isInstant(value)) {
            return { value: this.time(value) };
        }

        /* And so does a flag. `true` is what the wire says; Yes is what every other block on
         * these panes shows, and a cell reading `false` beside a row reading No would be the
         * same fact written two ways on one alert. */
        if (typeof value === 'boolean') {
            return { value: this.bool(value) };
        }

        return {
            value: typeof value === 'object' && value !== null ? JSON.stringify(value) : this.text(value),
            mono: true,
        };
    }

    /**
     * Turns a detector's field name into a readable label: `routerId` -> "Router ID",
     * `dial_failures` -> "Dial failures", `sourceIpCidr` -> "Source IP CIDR".
     *
     * Derived rather than looked up, because `labels` and `evidence` are flattened fields
     * so a detector can add a dimension without a mapping migration - a hand-maintained
     * table would drift exactly where it hides things. A key that is already prose is left
     * alone, and a unit spelled out at the end becomes a suffix, so `p95Millis` reads
     * "P95 (ms)".
     */
    private humanize(key: string): string {
        const words = key
            /* camelCase and PascalCase boundaries, including the ID in `routerIDs`. */
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
            /* snake_case, kebab-case, dotted paths. */
            .replace(/[_\-.]+/g, ' ')
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0);

        if (words.length === 0) {
            return key;
        }

        /* Only when something is left to label. A key that is nothing but the unit keeps
         * it as the name, because "(ms)" on its own names no measurement. */
        const unit = words.length > 1 ? UNIT_SUFFIXES[words[words.length - 1].toLowerCase()] : undefined;
        const labelWords = isNil(unit) ? words : words.slice(0, -1);

        const label = labelWords
            .map((word, index) => {
                if (ACRONYMS.has(word.toLowerCase())) {
                    return word.toUpperCase();
                }
                /* Only the first word is capitalised — sentence case, not Title Case,
                 * so a long key reads as a phrase rather than a headline. */
                if (index === 0) {
                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                }
                return word.toLowerCase();
            })
            .join(' ');

        return isNil(unit) ? label : `${label} (${unit})`;
    }

    private text(value: any): string {
        if (isNil(value) || value === '') {
            return EMPTY;
        }
        return `${value}`;
    }

    /** `false` has to render as "No", so it cannot go through the nil-or-empty check. */
    private bool(value: boolean | undefined): string {
        return isNil(value) ? EMPTY : value ? 'Yes' : 'No';
    }

    private time(value: string | undefined): string {
        if (isNil(value) || value === '') {
            return EMPTY;
        }
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? `${value}` : parsed.toLocaleString();
    }
}
